// auth.js
import React from 'react';
import * as c from '../helpers/constants';
import { parseISO, formatDistance } from 'date-fns';

export const TOKEN_KEY  = '@mosaite-token@';
export const TOKEN_DATE = '@mosaite-date@';
export const VALIDATE_TOKEN = '30'; // 30 minutos

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getHourToken = () => localStorage.getItem(TOKEN_DATE);

/* ---------- Normalizações locais (não afetam outros arquivos) ---------- */
const normalizeRoles = (roles) => {
  if (!roles) return [];
  if (Array.isArray(roles)) return roles.filter(Boolean);
  if (typeof roles === 'string') {
    return roles
      .split(/[,\s]+/)     // separa por vírgulas e/ou espaços
      .map(s => s.trim())
      .filter(Boolean);
  }
  return [];
};

// >>> compat: sempre produzir { roles: [...], papel: [...] }
const normalizeAuthResponse = (json = {}) => {
  const rolesArr = normalizeRoles(json.roles);
  const papelArr = normalizeRoles(json.papel || rolesArr);
  return {
    ...json,
    tokenType: json.tokenType || 'Bearer',
    roles: rolesArr,
    papel: papelArr,
  };
};

/* ------------------------------- Token --------------------------------- */
export const isTokenValido = () => {
  const tokenDateIso = getHourToken();
  if (tokenDateIso) {
    const agora = new Date();
    const dataToken = parseISO(tokenDateIso);
    const distance = formatDistance(agora, dataToken); // ex: "15 minutes"
    const tempLogin = distance.split('minutes')[0].trim(); // legado
    return Number.parseInt(tempLogin) < Number.parseInt(VALIDATE_TOKEN);
  }
  return false;
};

export const isAuthenticated = () => {
  const autenticado = getToken() != null && getToken() !== 'null';

  if (!autenticado) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_DATE);
    const sair = document.getElementById('btSair');
    if (sair) sair.click();
  }
  return autenticado;
};

/* ----------------------------- Redux bits ------------------------------ */
const constants = {
  LOGIN_REQUEST:  'AUTH_LOGIN_REQUEST',
  LOGIN_SUCCESS:  'AUTH_LOGIN_SUCCESS',
  LOGIN_FAILURE:  'AUTH_LOGIN_FAILURE',
  LOGIN_FINISHED: 'AUTH_LOGIN_FINISHED',

  LOGOUT_REQUEST:  'AUTH_LOGOUT_REQUEST',
  LOGOUT_SUCCESS:  'AUTH_LOGOUT_SUCCESS',
  LOGOUT_FAILURE:  'AUTH_LOGOUT_FAILURE',
  LOGOUT_FINISHED: 'AUTH_LOGIN_FINISHED',

  TROCA_SENHA: 'AUTH_LOGIN_TROCA_SENHA',
};

// >>> estado inicial garante user.roles/papel como []
const initialState = {
  isAuthenticated: false,
  isLoading: false,
  exception: {},
  user: { roles: [], papel: [] },
};

export const authActionPublic = {
  login: (user) => async (dispatch) => {
    if (isAuthenticated() && isTokenValido()) {
      throw c.MESSAGE_ERROR('Usuário já autenticado.');
    }

    user.ipUsuarioLogado = await fetch('https://meuip.montreal.com.br?format=json')
      .then(res => res.json())
      .then(json => json.ip);

    const url = `${c.API_AUTH_URL}/login`;
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user),
    };

    dispatch({ type: constants.LOGIN_REQUEST });

    return fetch(url, options)
      .then(async (response) => {
        const body = await readBodySafe(response);

        if (!response.ok) {
          const message =
            body?.detalMessage || body?.detailMessage || body?.message || 'Falha de autenticação.';
          const ex = c.MESSAGE_ERROR(message, { status: response.status, body });
          throw ex;
        }

        const json = normalizeAuthResponse(body || {});

        if (json.firstAccess === true) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(TOKEN_DATE);
          dispatch({ type: constants.TROCA_SENHA, user: json });
          return json; 
        }

        if (json.accessToken != null && json.accessToken !== 'null') {
          localStorage.setItem(TOKEN_KEY, json.accessToken);
          localStorage.setItem(TOKEN_DATE, json.date || new Date().toISOString());
          dispatch({ type: constants.LOGIN_SUCCESS, user: json });
          return json;
        } else {
          dispatch({ type: constants.TROCA_SENHA, user: json });
          return json;
        }
      })
      .catch((err) => {
        const isNormalized = err && err.type === 'error' && typeof err.message === 'string';
        if (isNormalized) {
          dispatch({ type: constants.LOGIN_FAILURE, exception: err });
          throw err;
        }
        const ex = c.MESSAGE_ERROR('Falha de conexão com o serviço de autenticação.', { raw: String(err) });
        dispatch({ type: constants.LOGIN_FAILURE, exception: ex });
        throw ex;
      });
  },

  loginFinished: () => async (dispatch) => {
    dispatch({ type: constants.LOGIN_FINISHED });
  },
};

export const authActionPrivate = {
  logout: () => async (dispatch) => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_DATE);
    dispatch({ type: constants.LOGOUT_SUCCESS });
  },

  logoutFinished: () => async (dispatch) => {
    dispatch({ type: constants.LOGOUT_FINISHED });
  },
};

export const reducer = (state, action) => {
  state = state || initialState;

  switch (action.type) {
    case constants.LOGIN_REQUEST:
      return {
        isAuthenticated: false,
        isLoading: true,
        exception: {},
        user: { roles: [], papel: [] }, // <<< SEMPRE arrays
      };

    case constants.LOGIN_SUCCESS:
      return {
        isAuthenticated: true,
        isLoading: true,
        exception: {},
        // garante papel/roles como arrays mesmo se alguém quebrar a normalização
        user: {
          ...action.user,
          roles: normalizeRoles(action.user?.roles),
          papel: normalizeRoles(action.user?.papel || action.user?.roles),
        },
      };

    case constants.LOGIN_FAILURE:
      return {
        isAuthenticated: false,
        isLoading: false,
        exception: action.exception,
        user: { roles: [], papel: [] },
      };

    case constants.LOGIN_FINISHED:
      return {
        ...state,
        isLoading: false,
      };

    case constants.LOGOUT_REQUEST:
      return {
        isAuthenticated: true,
        isLoading: true,
        exception: {},
        user: state.user || { roles: [], papel: [] },
      };

    case constants.LOGOUT_SUCCESS:
      return {
        isAuthenticated: false,
        isLoading: true,
        exception: {},
        user: { roles: [], papel: [] },
      };

    case constants.LOGOUT_FAILURE:
      return {
        isAuthenticated: true,
        isLoading: false,
        exception: action.exception,
        user: state.user || { roles: [], papel: [] },
      };

    case constants.LOGOUT_FINISHED:
      return {
        ...state,
        isLoading: false,
      };

    case constants.TROCA_SENHA:
      return {
        isAuthenticated: false,
        isLoading: true,
        exception: {},
        user: {
          ...(action.user || {}),
          roles: normalizeRoles(action.user?.roles),
          papel: normalizeRoles(action.user?.papel || action.user?.roles),
        },
      };

    default:
      return state;
  }
};

// >>> garante token sempre string (evita undefined.includes)
const tokenInitial = { token: '' };

const readBodySafe = async (response) => {
  const ct = (response.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('json')) {
    try { return await response.json(); } catch { return null; }
  }
  try { const t = await response.text(); return t ? { detalMessage: t } : null; } catch { return null; }
};

export const reducerToken = (state, action) => {
  state = state || tokenInitial;

  switch (action.type) {
    case constants.LOGIN_SUCCESS:
      return {
        token: `${action.user.tokenType || 'Bearer'} ${action.user.accessToken}`,
      };
    default:
      return state;
  }
};

export { constants };
